import { create } from 'zustand';
import { getDocuments } from '../api/generated/documents/documents';
import type { FacilityFlowCoreModulesDocumentsDtosDocumentDto } from '../api/generated/models';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import i18n from '@/i18n/config';

export type DocumentDto = FacilityFlowCoreModulesDocumentsDtosDocumentDto;

/** Mirrors the backend DocumentOwnerType enum (sent as a string over the wire). */
export type DocumentOwnerType = 'Customer' | 'Object';

const {
  postApiV1Documents,
  getApiV1Documents,
  getApiV1DocumentsIdContent,
  deleteApiV1DocumentsId,
} = getDocuments();

interface DocumentState {
  documents: DocumentDto[];
  loading: boolean;
  uploading: boolean;
  error: string | null;

  fetchDocuments: (ownerType: DocumentOwnerType, ownerId: string) => Promise<void>;
  uploadDocument: (
    ownerType: DocumentOwnerType,
    ownerId: string,
    file: File,
    description?: string,
    tags?: string[],
  ) => Promise<void>;
  downloadDocument: (doc: DocumentDto) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
}

export const useDocumentStore = create<DocumentState>((set) => ({
  documents: [],
  loading: false,
  uploading: false,
  error: null,

  fetchDocuments: async (ownerType, ownerId) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1Documents({ ownerType, ownerId });
      set({ documents: data ?? [], loading: false });
    } catch (error) {
      // Fetch failures render as an inline EmptyState (no toast), per the app convention.
      showApiError(error);
      set({ error: i18n.t('documents:errors.fetchFailed'), loading: false });
    }
  },

  uploadDocument: async (ownerType, ownerId, file, description, tags) => {
    set({ uploading: true });
    try {
      const created = await postApiV1Documents({
        file,
        ownerType,
        ownerId,
        description: description || undefined,
        tags: tags && tags.length > 0 ? tags.join(',') : undefined,
      });
      set((state) => ({ documents: [created, ...state.documents], uploading: false }));
      useToastStore.getState().addToast(i18n.t('documents:messages.uploaded'), 'success');
    } catch (error) {
      set({ uploading: false });
      showApiError(error);
      throw error;
    }
  },

  downloadDocument: async (doc) => {
    if (!doc.id) return;
    try {
      const blob = await getApiV1DocumentsIdContent(doc.id);
      const url = URL.createObjectURL(blob as Blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.name ?? 'download';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      showApiError(error);
    }
  },

  deleteDocument: async (id) => {
    try {
      await deleteApiV1DocumentsId(id);
      set((state) => ({ documents: state.documents.filter((d) => d.id !== id) }));
      useToastStore.getState().addToast(i18n.t('documents:messages.deleted'), 'success');
    } catch (error) {
      showApiError(error);
      throw error;
    }
  },
}));

export default useDocumentStore;
